import React, { useState, useEffect, SyntheticEvent, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import qs from 'query-string'
import { Formik, FormikProps, Form } from 'formik'
import * as yup from 'yup'
import _ from 'lodash'
import { MediaData } from 'components/MediaViewer'
import Text from 'particles/Text'
import { useHistoryPushWithForwardedParams, useNewTranslation, useWindowSize } from 'hooks'
import SideNavigator from 'particles/SideNavigator'
import Toast from 'particles/Toast'
import { TemplateContainer } from '../style'
import CampaignInformation from './CampaignInformation'
import CampaignSchedule from './CampaignSchedule'
import Icon from 'components/Icon/Icon'
import { SmallScreenSideBar } from 'components/Campaign/Editor/EditTask/styles'
import {
    getTemplateDetail,
    getTemplateData,
    selectTemplateDetail,
    getCampaignInfo,
    selectCampaignInfo,
    getCampaignSchedule,
    selectCampaignSchedule,
    getCampaignTaskElements,
    selectCampaignTasks,
    selectTemplateData,
    clearTemplateDetail,
    clearTemplateData,
    clearCampaignInfo,
    clearCampaignSchedule,
    clearCampaignTaskElements,
    OBJECTIVE,
} from 'state/campaignTemplates'
import { CampaignTaskElements, TemplateDetail as TemplateDetailData } from 'services/api/campaignTemplates'
import { COUNTRY_ENUM } from 'services/api/constants'
import CampaignDetail from 'components/AppPreview/CampaignDetail'
import { updatePurchaseStep1Data } from 'state/appPreview/action/purchase'
import { updateTiktokRepostStep1Data } from 'state/appPreview/action/tiktokRepost'
import PreviewContainer from 'components/AppPreview/common/PreviewContainer'
import IconButton from 'particles/Button/IconButton'
import { requestProfileOptions } from 'state/constants'
import { updateCampaignInformationData } from 'state/appPreview/action/campaign'
import PurchaseProduct from 'components/AppPreview/Purchase'
import { selectTemplateFirstStepDataByTaskIndex } from 'state/appPreview/selector'
import IGReels from 'components/AppPreview/IGReels'
import { updateIgReelsStep1Data } from 'state/appPreview/action/igReels'
import IGStoryRepostPreview from 'components/AppPreview/IGStoryRepost'
import IGStoryNonUgcPreview from 'components/AppPreview/IGStoryNonUgc'
import { updateigStoryRepostStep1Data } from 'state/appPreview/action/igStoryRepost'
import TiktokRepostPreview from 'components/AppPreview/TiktokRepost'
import TikTok from 'components/AppPreview/TikTok'
import { clearAppPreviewData } from 'state/appPreview/action'
import { getTaskValidationSchema } from '../Tasks/validationSchema'
import { ElementKeys, fieldKeyByTasks, getElementsOptions, getOptions } from './options'
import PurchaseTask from '../Tasks/PurchaseTask'
import IGReelsTask from '../Tasks/IGReelsTask'
import TiktokRepost from '../Tasks/TiktokRepost'
import IGStoryRepost from '../Tasks/IGStoryRepost'
import { TaskElementProps } from '../Tasks/Actions'
import IGStoryNonUGCTask from '../Tasks/IGStoryNonUGCTask'
import { updateIgStoryNonUgcStep1Data } from 'state/appPreview/action/igStoryNonUgc'
import IGPostTask from '../Tasks/IGPostTask'
import TiktokTask from '../Tasks/TiktokTask'
import ReviewTask from '../Tasks/ReviewTask'
import FollowTask from '../Tasks/FollowTask'
import ReviewTextImagePreview from 'components/AppPreview/ReviewTextImage'
import { updateReviewTextImageStep1Data } from 'state/appPreview/action/reviewTextImage'
import DownloadAppTask from '../Tasks/DownloadAppTask'
import { runValidateSchema } from 'components/utils'
import IGPost from 'components/AppPreview/IGPost'
import { updateTikTokStep1Data } from 'state/appPreview/action/tikTok'
import { updateIgPostStep1Data } from 'state/appPreview/action/igPost'
import { updateDownloadAppStep1Data } from 'state/appPreview/action/downloadApp'
import DownloadAppPreview from 'components/AppPreview/DownloadApp'
import { selectTemplateFormErrors } from 'state/error/campaignTemplates/selectors'
import CommentsCenter from 'components/Campaign/CommentsCenter'
import { useIdentifiersMap, useToggleCampaignEditorDrawer } from 'hooks/comment'
import TemplateActions from '../TemplateActions'
import { selectCampaignByTemplateData } from 'state'
import { useCheckEditTemplatedCampaignPermission } from 'hooks/permissions'
import { OnFormikValueChangeProps } from '../OnFormikValueChange'
import { CAMPAIGN_STATUS_ENUM } from 'state/campaigns'
import { RecordChangesKey } from 'components/Shared/CompareChangesPopup'
import { setConfirmPopup } from 'state/confirmPopup'
import IGStoryUGC from '../Tasks/IGStoryUGC'
import IGStoryUGCPReview from 'components/AppPreview/IGStoryUGC'
import { parseBackendDate } from 'services/dates'
import FollowPreview from 'components/AppPreview/Follow'
import CampaignStatusLabel from '../CampaignStatusLabel'
import VideoCreationTask from '../Tasks/VideoCreationTask'
import VideoCreationPreview from 'components/AppPreview/VideoCreation'
import PhotoCreationTask from '../Tasks/PhotoCreationTask'
import PhotoCreationPreview from 'components/AppPreview/PhotoCreation'
import InfoTooltip from 'components/InfoTooltip'
import { TEMPLATE_TAB_ID } from '..'

const previewTask: Partial<Record<ElementKeys, React.FC>> = {
    purchase: PurchaseProduct,
    ig_reels: IGReels,
    tiktok_repost: TiktokRepostPreview,
    ig_story_repost: IGStoryRepostPreview,
    tiktok: TikTok,
    ig_story_non_ugc_sticker_link: IGStoryNonUgcPreview,
    ig_post: IGPost,
    review_text_image_approval: ReviewTextImagePreview,
    review_text_image_approval_purchase: ReviewTextImagePreview,
    download_app: DownloadAppPreview,
    download_app_sign_up_without_id: DownloadAppPreview,
    review_text: ReviewTextImagePreview,
    review_text_approval: ReviewTextImagePreview,
    ig_story_ugc: IGStoryUGCPReview,
    review_text_purchase: ReviewTextImagePreview,
    follow: FollowPreview,
    video_creation: VideoCreationPreview,
    photo_creation: PhotoCreationPreview,
}

const taskElements: Record<ElementKeys, React.FC<TaskElementProps>> = {
    purchase: PurchaseTask,
    ig_reels: IGReelsTask,
    tiktok_repost: TiktokRepost,
    ig_story_repost: IGStoryRepost,
    download_app: DownloadAppTask,
    download_app_sign_up_without_id: DownloadAppTask,
    ig_post: IGPostTask,
    ig_story_non_ugc_sticker_link: IGStoryNonUGCTask,
    review_text_image_approval: ReviewTask,
    review_text_image_approval_purchase: ReviewTask,
    tiktok: TiktokTask,
    ig_story_ugc: IGStoryUGC,
    review_text: ReviewTask,
    review_text_approval: ReviewTask,
    review_text_purchase: ReviewTask,
    follow: FollowTask,
    video_creation: VideoCreationTask,
    photo_creation: PhotoCreationTask,
}

export interface TemplateDetailSchema {
    title: string | null
    categories: number[] | null
    images: MediaData[] | null
    use_default_image: boolean
    registration_enable: boolean | null
    start_time: string | null
    payout_deadline: string | null
    task_elements: CampaignTaskElements[]
}

interface TemplateDetailProps {
    setSelectedTab: React.Dispatch<React.SetStateAction<TEMPLATE_TAB_ID>>
}

const TemplateDetail: React.FC<TemplateDetailProps> = ({ setSelectedTab }) => {
    const dispatch = useDispatch()
    const [t] = useNewTranslation()
    const params = qs.parse(location.search)
    const historyPush = useHistoryPushWithForwardedParams()
    const { templateData: templateDataUuid } = params
    const options = getOptions(t)
    const templateDetail = useSelector(selectTemplateDetail)
    const templateData = useSelector(selectTemplateData)
    const campaignInfo = useSelector(selectCampaignInfo)
    const templateFormErrors = useSelector(selectTemplateFormErrors)
    const campaignSchedule = useSelector(selectCampaignSchedule)
    const campaignTask = useSelector(selectCampaignTasks)
    const [stepOptions, setStepOptions] = useState(options)
    const [selected, setSelected] = useState<number>(0)
    const [openInfoToast, setOpenInfoToast] = useState(false)
    const [infoToastMessage, setInfoToastMessage] = useState('')
    const [showError, setShowError] = useState(false)
    const [indexToTaskKey, setIndexToTaskKey] = useState<Record<number, ElementKeys>>({})
    const [showSidebarTask, setShowSidebarTask] = useState(false)
    const [selectedTaskElementUuid, setSelectedTaskElementUuid] = useState<string>('')
    const elementOptions = getElementsOptions(t)
    const [isDisableNavigation, setIsDisableNavigation] = useState(false)
    const [showDrawer, setShowDrawer] = useState<boolean>(false)
    const searchParams = new URLSearchParams(location.search)
    const currentDrawerParam = searchParams.get('drawer')
    const isCommentCenterOpened = currentDrawerParam === 'comments'
    const isAppPreviewOpened = currentDrawerParam === 'preview'
    const [stepNumberByTaskIndex, setStepNumberByTaskIndex] = useState<number[]>([])
    const selectedTemplateFirstStepData = useSelector(selectTemplateFirstStepDataByTaskIndex(selected - 2))
    const campaign = useSelector(selectCampaignByTemplateData)
    const isPermissionEditable = useCheckEditTemplatedCampaignPermission((campaign && campaign.status) || undefined)
    const isReadOnly = !isPermissionEditable
    const windowSize = useWindowSize()
    const isMobileSize = windowSize <= 768
    const formContent = useRef<HTMLDivElement | null>(null)
    const isPublishedCampaign =
        campaign?.status === CAMPAIGN_STATUS_ENUM.LIVE || campaign?.status === CAMPAIGN_STATUS_ENUM.CLOSED

    // generate list of key to skip validation for task elements
    const taskKeysToSkipValidation = useMemo(() => {
        if (!templateDetail?.task_elements) {
            return []
        }

        return templateDetail?.task_elements.flatMap((element, idx) => {
            const elementKey = element.key as ElementKeys
            const placeholderKeys = element.placeholder_keys || []

            const fieldKeys = fieldKeyByTasks[elementKey] || []
            const keysExcluded = _.xor(fieldKeys, placeholderKeys)
                .filter(result => result !== 'element')
                .map(result => `${idx}.${result}`)

            return keysExcluded
        })
    }, [templateDetail])

    const updateToggleCampaignEditorDrawer = useToggleCampaignEditorDrawer()
    const { selectedIdentifier } = params
    const { campaignTemplateIdentifierMap, unresolvedTemplateFieldIdentifiers } = useIdentifiersMap()

    useEffect(() => {
        setStepOptions(prevState => {
            const newOptions = prevState.map(option => ({
                ...option,
                isCommented: option.id ? option.isCompleted && option.id in unresolvedTemplateFieldIdentifiers : false,
            }))

            // Only update if there's a change
            const prevOptionsStr = JSON.stringify(prevState)
            const newOptionsStr = JSON.stringify(newOptions)

            if (prevOptionsStr !== newOptionsStr) {
                return newOptions
            }
            return prevState // Return previous state if no changes
        })
    }, [unresolvedTemplateFieldIdentifiers])

    useEffect(() => {
        if (typeof selectedIdentifier === 'string' && campaignTemplateIdentifierMap[selectedIdentifier]) {
            const { stepKey } = campaignTemplateIdentifierMap[selectedIdentifier]
            const step =
                stepKey === 'campaign_info'
                    ? 0
                    : stepKey === 'campaign_schedule'
                      ? 1
                      : Object.keys(indexToTaskKey).find((key: any) => indexToTaskKey[key] === stepKey)
            if (step === undefined) return
            const stepNumber = Number(step)
            if (selectedIdentifier && stepNumber !== selected) {
                onSelectStep(stepNumber)
            }
        }
    }, [selectedIdentifier])

    useEffect(() => {
        // Make sure the drawer is open or close if param is available
        setShowDrawer(Boolean(currentDrawerParam))
    }, [updateToggleCampaignEditorDrawer])

    useEffect(() => {
        if (
            selectedTemplateFirstStepData?.taskElementUuid &&
            selectedTemplateFirstStepData?.taskElementUuid != selectedTaskElementUuid &&
            selected >= 2
        ) {
            // only update step 1 preview if it's task section
            if (indexToTaskKey[selected] === 'purchase') {
                const payload = {
                    taskTitle: selectedTemplateFirstStepData?.taskTitle,
                    stepTitle: selectedTemplateFirstStepData?.step?.title,
                    title: selectedTemplateFirstStepData?.step?.product_name,
                }
                dispatch(updatePurchaseStep1Data(payload))
            } else if (indexToTaskKey[selected] === 'ig_story_repost') {
                const payload = {
                    taskTitle: selectedTemplateFirstStepData?.taskTitle,
                    stepTitle: selectedTemplateFirstStepData?.step?.title,
                }
                dispatch(updateigStoryRepostStep1Data(payload))
            } else if (indexToTaskKey[selected] === 'tiktok_repost') {
                const payload = {
                    taskTitle: selectedTemplateFirstStepData?.taskTitle,
                    stepTitle: selectedTemplateFirstStepData?.step?.title,
                    title: selectedTemplateFirstStepData?.step?.blocks?.[0]?.title ?? '',
                    urlBlockTitle: selectedTemplateFirstStepData?.step?.blocks?.[1].title ?? '',
                    mediaBlockTitle: selectedTemplateFirstStepData?.step?.blocks?.[2].title ?? '',
                    mediaBlockMaxPhotos: selectedTemplateFirstStepData?.step?.blocks?.[2]?.max_photos ?? 0,
                }
                dispatch(updateTiktokRepostStep1Data(payload))
            } else if (indexToTaskKey[selected] === 'ig_reels') {
                const payload = {
                    taskTitle: selectedTemplateFirstStepData?.taskTitle,
                    stepTitle: selectedTemplateFirstStepData?.step?.title,
                }
                dispatch(updateIgReelsStep1Data(payload))
            } else if (indexToTaskKey[selected] === 'tiktok') {
                const payload = {
                    taskTitle: selectedTemplateFirstStepData?.taskTitle,
                    stepTitle: selectedTemplateFirstStepData?.step?.title,
                }
                dispatch(updateTikTokStep1Data(payload))
            } else if (indexToTaskKey[selected] === 'ig_story_non_ugc_sticker_link') {
                const payload = {
                    taskTitle: selectedTemplateFirstStepData?.taskTitle,
                    stepTitle: selectedTemplateFirstStepData?.step?.title,
                }
                dispatch(updateIgStoryNonUgcStep1Data(payload))
            } else if (indexToTaskKey[selected] === 'ig_post') {
                const payload = {
                    taskTitle: selectedTemplateFirstStepData?.taskTitle,
                    stepTitle: selectedTemplateFirstStepData?.step?.title,
                }
                dispatch(updateIgPostStep1Data(payload))
            } else if (
                indexToTaskKey[selected] === 'review_text_image_approval' ||
                indexToTaskKey[selected] === 'review_text_image_approval_purchase'
            ) {
                const payload = {
                    taskTitle: selectedTemplateFirstStepData?.taskTitle,
                    title: selectedTemplateFirstStepData?.step?.blocks?.[0]?.title ?? '',
                    mediaBlockTitle: selectedTemplateFirstStepData?.step?.blocks?.[1].title ?? '',
                    mediaBlockMaxPhotos: selectedTemplateFirstStepData?.step?.blocks?.[1]?.max_photos ?? 0,
                }
                dispatch(updateReviewTextImageStep1Data(payload))
            } else if (
                indexToTaskKey[selected] === 'download_app_sign_up_without_id' ||
                indexToTaskKey[selected] === 'download_app'
            ) {
                const payload = {
                    taskTitle: selectedTemplateFirstStepData?.taskTitle,
                    stepTitle: selectedTemplateFirstStepData?.step?.title,
                    mediaBlockTitle: selectedTemplateFirstStepData?.step?.blocks?.[1].title ?? '',
                    mediaBlockMaxPhotos: selectedTemplateFirstStepData?.step?.blocks?.[1]?.max_photos ?? 0,
                    textBlockTitle: selectedTemplateFirstStepData?.step?.blocks?.[2]?.title ?? '',
                }
                dispatch(updateDownloadAppStep1Data(payload))
            } else if (indexToTaskKey[selected] === 'follow') {
                const payload = {
                    taskTitle: selectedTemplateFirstStepData?.taskTitle,
                    title: selectedTemplateFirstStepData?.step?.blocks?.[0]?.title ?? '',
                    mediaBlockTitle: selectedTemplateFirstStepData?.step?.blocks?.[1].title ?? '',
                    mediaBlockMaxPhotos: selectedTemplateFirstStepData?.step?.blocks?.[1]?.max_photos ?? 0,
                }
                dispatch(updateReviewTextImageStep1Data(payload))
            }
            setSelectedTaskElementUuid(selectedTemplateFirstStepData?.taskElementUuid)
        }
    }, [selectedTemplateFirstStepData, indexToTaskKey, selected])

    useEffect(() => {
        if (campaignInfo) {
            dispatch(updateCampaignInformationData(campaignInfo))
        }
    }, [campaignInfo])

    useEffect(() => {
        if (templateDataUuid && typeof templateDataUuid === 'string') {
            dispatch(
                getTemplateData({
                    templateDataUuid: templateDataUuid,
                }),
            )
            dispatch(
                getCampaignInfo({
                    templateDataUuid: templateDataUuid,
                }),
            )
            dispatch(
                getCampaignSchedule({
                    templateDataUuid: templateDataUuid,
                }),
            )
            dispatch(
                getCampaignTaskElements({
                    templateDataUuid: templateDataUuid,
                }),
            )
        }
        return () => {
            dispatch(clearTemplateDetail())
            dispatch(clearTemplateData())
            dispatch(clearCampaignInfo())
            dispatch(clearCampaignSchedule())
            dispatch(clearCampaignTaskElements())
            dispatch(clearAppPreviewData())
            setStepOptions(options)
            setIndexToTaskKey({})
        }
    }, [dispatch, templateDataUuid])

    useEffect(() => {
        if (templateData?.template.uuid) {
            dispatch(
                getTemplateDetail({
                    templateUuid: templateData?.template.uuid,
                }),
            )
        }
    }, [dispatch, templateData])

    useEffect(() => {
        updateStepNumberByTaskIndex(templateDetail)
        insertTaskOnSideBar(templateDetail)
    }, [templateDetail?.task_elements, templateFormErrors])

    useEffect(() => {
        if (templateDetail?.country)
            dispatch(requestProfileOptions({ country: templateDetail?.country as COUNTRY_ENUM }))
    }, [templateDetail?.country])

    const updateStepNumberByTaskIndex = (templateDetail?: TemplateDetailData) => {
        const templateElements = templateDetail?.task_elements || []
        if (templateElements.length) {
            const stepNumberByTaskIndex: number[] = []
            templateElements.forEach(element => {
                const stepNumber = element.data.steps.length
                stepNumberByTaskIndex.push(stepNumber)
            })
            setStepNumberByTaskIndex(stepNumberByTaskIndex)
        }
    }

    const insertTaskOnSideBar = (templateDetail?: TemplateDetailData) => {
        const templateElements = templateDetail?.task_elements || []
        if (templateElements.length) {
            const newStepOptions = [...stepOptions.slice(0, 2)]
            const newIndexTaskKey = { ...indexToTaskKey }
            let currIndex = newStepOptions.length - 1
            templateElements.forEach(element => {
                const selectedElementOption = elementOptions[element.key as ElementKeys]
                if (selectedElementOption) {
                    newStepOptions.push(selectedElementOption)
                    currIndex++
                    newIndexTaskKey[currIndex] = element.key as ElementKeys
                }
            })
            setIndexToTaskKey(newIndexTaskKey)
            setStepOptions(newStepOptions)
        }
    }

    if (!campaignInfo || !campaignSchedule || _.isEmpty(campaignTask) || !templateDetail?.country) {
        return <></>
    }

    const initialValues: TemplateDetailSchema = {
        title: campaignInfo?.title ?? null,
        categories: campaignInfo?.categories ?? null,
        images: campaignInfo?.images ?? null,
        registration_enable:
            // app promotion don't need registration, so backend request to pass false as value
            templateDetail?.objective === OBJECTIVE.appPromotion || templateDetail?.key === 'follow'
                ? false
                : campaignInfo?.registration_enable ?? null,
        use_default_image: campaignInfo?.use_default_image ?? false,
        start_time: campaignSchedule?.start_time ?? null,
        payout_deadline: campaignSchedule?.payout_deadline
            ? parseBackendDate(campaignSchedule?.payout_deadline, templateDetail?.country, {
                  showTime: false,
                  showYear: true,
                  addSeconds: -1,
              })
            : null,
        task_elements: campaignTask ?? [],
    }
    const campaignEndTime =
        campaignSchedule && campaignSchedule.campaign_time_window && campaignSchedule.campaign_time_window.end
            ? new Date(
                  parseBackendDate(campaignSchedule.campaign_time_window.end, templateDetail?.country, {
                      showTime: true,
                      showYear: false,
                      addSeconds: -1,
                  }),
              )
            : null
    const validationSchema = yup.object({
        title: yup
            .string()
            .nullable()
            .required(t('campaign.template.campaignName.errorMessage.empty'))
            .max(100, t('errorMessage.validation.enterCampaignNameCharacterLimit')),
        categories: yup
            .array()
            .nullable()
            .required(t('errorMessage.validation.selectCampaignCategory'))
            .max(2, t('errorMessage.validation.selectCategoryLimit')),
        use_default_image: yup.boolean().default(false),
        images: yup
            .array()
            .nullable()
            .when('use_default_image', {
                is: false,
                then: yup.array().nullable().required(t('errorMessage.validation.uploadCampaignBanner')),
            }),
        registration_enable: yup.boolean().nullable().required(t('errorMessage.validation.emptySelection')),
        start_time: yup
            .string()
            .nullable()
            .required(t('errorMessage.validation.datePicker.selectDate'))
            .test('not-past', t('errorMessage.businessLogic.cantEnterPastDate'), function (value) {
                const today = new Date()
                today.setHours(0, 0, 0, 0)
                return new Date(value) >= today
            }),
        payout_deadline: yup
            .string()
            .nullable()
            .required(t('errorMessage.validation.datePicker.selectDate'))
            .test('not-past', t('errorMessage.businessLogic.cantEnterPastDate'), function (value) {
                const today = new Date()
                today.setHours(0, 0, 0, 0)
                return new Date(value) >= today
            })
            .when([], () => {
                if (campaignEndTime) {
                    return yup
                        .date()
                        .nullable()
                        .required(t('errorMessage.validation.datePicker.selectDate'))
                        .min(campaignEndTime, t('errorMessage.businessLogic.payoutBeforeEndDate'))
                } else {
                    return
                }
            }),
        task_elements: yup.array().of(getTaskValidationSchema(t)),
    })

    const toNextSection = (isCompleted: boolean, isError: boolean) => {
        updateStepStatus(selected, isCompleted, isError)

        if (selected + 1 < stepOptions.length) {
            setSelected(selected + 1)
        }
    }

    const backPreviousSection = () => {
        if (selected - 1 >= 0) {
            setSelected(selected - 1)
        }
    }

    const updateCurrentStepStatus = (isCompleted: boolean, isError: boolean) => {
        setStepOptions(prev => {
            return prev.map((option, index) => {
                if (index === selected) {
                    return { ...option, isCompleted: isCompleted, isError: isError }
                } else if (index === selected + 1) {
                    return { ...option, isDisabled: false }
                }
                return option
            })
        })
    }

    const updateStepStatus = (completeStepIndex: number, isCompleted: boolean, isError: boolean) => {
        const newStepOptions = stepOptions.map((option, index) => {
            if (index === completeStepIndex) {
                return { ...option, isCompleted: isCompleted, isError: isError }
            } else if (index === completeStepIndex + 1) {
                return { ...option, isDisabled: false }
            }
            return option
        })
        setStepOptions(newStepOptions)
    }

    const onSelectStep = (index: number, isDisabled?: boolean) => {
        const isTemplateValueChange = localStorage.getItem(RecordChangesKey.IS_CAMPAIGN_VALUE_CHANGE)
        if (isDisabled) {
            setInfoToastMessage(t('campaign.template.step1.tooltips.fillRequiredField'))
            setOpenInfoToast(true)
        } else {
            if (isTemplateValueChange === 'yes') {
                dispatch(
                    setConfirmPopup({
                        show: true,
                        onConfirm: () => {
                            setSelected(index)
                        },
                    }),
                )
            } else {
                setSelected(index)
            }
        }
        if (formContent.current) {
            formContent.current.scrollTo({
                top: 0,
            })
        }
    }

    const handleClose = (event: SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
            return
        }
        setOpenInfoToast(false)
    }

    const validateForm = (
        formikBag: FormikProps<TemplateDetailSchema>,
        validateCompleteOnly: boolean, // set to true if it's initail validaiton. we'll only show dot error if user input invalid value or click last button
        successCallback?: () => void,
        errorCallback?: () => void,
    ) => {
        // some task have field that doesn't want to be validate, it will be use in app preview
        // the code below to delete validation item that are listed in taskKeysToSkipValidation
        const valuesToValidate = _.cloneDeep(formikBag.values)

        if (taskKeysToSkipValidation?.length) {
            taskKeysToSkipValidation.forEach(key => {
                const [idx, fieldKey] = key.split('.')
                if (valuesToValidate.task_elements[+idx][fieldKey as keyof CampaignTaskElements]) {
                    delete valuesToValidate.task_elements[+idx][fieldKey as keyof CampaignTaskElements]
                }
            })
        }

        formikBag.validateForm(valuesToValidate).then(errors => {
            const taskErrors = (errors.task_elements || []) as Record<keyof CampaignTaskElements, string>[]
            // copy options and make default completed/error before checking validation result
            const copyOptions = stepOptions.map((item, index, array) => ({
                ...item,
                isCompleted: true,
                isError: false,
            }))
            if (errors.title || errors.categories || errors.images || errors.registration_enable) {
                copyOptions[0] = { ...copyOptions[0], isCompleted: isPublishedCampaign, isError: !validateCompleteOnly }
            }

            if (errors.start_time || errors.payout_deadline) {
                copyOptions[1] = { ...copyOptions[1], isCompleted: isPublishedCampaign, isError: !validateCompleteOnly }
            }
            taskErrors.map((taskError, idx) => {
                if (taskError) {
                    copyOptions[idx + 2] = {
                        ...copyOptions[idx + 2],
                        isCompleted: isPublishedCampaign,
                        isError: validateCompleteOnly ? false : true,
                    }
                }
            })
            setStepOptions(copyOptions)
            if (_.isEmpty(errors)) {
                successCallback?.()
            } else {
                errorCallback?.()
            }
        })
    }
    const navigateToPaymentSummary = () => {
        setSelectedTab('templateSummary')
        historyPush(
            `/campaign/template/templateSummary?templateData=${templateDataUuid}&campaign=${templateData?.campaign.uuid}`,
        )
    }

    const submitLastStep = (formikBag: FormikProps<TemplateDetailSchema>) => {
        navigateToPaymentSummary()
    }

    const openWarningClickToast = () => {
        setInfoToastMessage(t('campaign.template.toast.operationTooQuick'))
        setOpenInfoToast(true)
    }

    const onClickDrawer = (drawer: 'comments' | 'preview') => () => {
        setShowDrawer(!showDrawer)
        updateToggleCampaignEditorDrawer(drawer)
    }

    const handleClickSidebar = (() => {
        if (!isDisableNavigation) {
            return
        }
        return () => {
            openWarningClickToast()
        }
    })()

    const renderElementByKey = (
        idx: number,
        validateFields: OnFormikValueChangeProps['validateSpecificField'],
        formikBag: FormikProps<TemplateDetailSchema>,
    ) => {
        const elementKey = indexToTaskKey[idx]
        const isLastElement = idx === stepOptions.length - 1
        const Element = taskElements[elementKey] || null
        if (Element === null) {
            return null
        }
        return (
            <Element
                key={idx}
                onBack={backPreviousSection}
                onNext={isLastElement ? undefined : toNextSection}
                onSubmit={isLastElement ? () => submitLastStep(formikBag) : undefined}
                showError={showError}
                validateSpecificField={validateFields}
                taskOrder={idx - 2}
                updateCurrentStepStatus={updateCurrentStepStatus}
                templateUuid={!!templateDataUuid && typeof templateDataUuid === 'string' ? templateDataUuid : null}
                isDisableNavigation={isDisableNavigation}
                setNavigationDisable={setIsDisableNavigation}
                openWarningClickToast={openWarningClickToast}
                campaignTemplateStepKey={elementKey}
                isReadOnly={isReadOnly}
            />
        )
    }

    const isCampaignDetail = selected === 0 || selected === 1
    const elementKey = indexToTaskKey[selected]
    const PreviewElement = elementKey && elementKey in previewTask ? previewTask[elementKey] : null
    const stepNumber = selected > 1 ? stepNumberByTaskIndex[selected - 2] : undefined

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={() => {}}
            // use validate instead of validationSchema because of issue within lazy validation of certain fields
            // case found: app_name field cannot be correctly validated using validationSchema
            validate={values => runValidateSchema(values, validationSchema)}
        >
            {(formikBag: FormikProps<TemplateDetailSchema>): JSX.Element => {
                useEffect(() => {
                    if (campaignInfo && campaignSchedule && campaignTask.length > 0 && !_.isEmpty(indexToTaskKey)) {
                        // needs this setTimeout for formik value update
                        setTimeout(() => validateForm(formikBag, true), 200)
                    }
                }, [campaignInfo, campaignSchedule, campaignTask, indexToTaskKey])

                useEffect(() => {
                    // this is usecase when user click on template tab 3 (payment summary)
                    // it will trigger validate backend, but not yet mapping the error
                    // I use validateForm function to automatically map error based on validation schema,
                    // so we don't have to manually map error code and message like campaign editor
                    if (
                        !!templateFormErrors.task_element_data ||
                        !!templateFormErrors.campaign_schedule ||
                        !!templateFormErrors.campaign_info ||
                        !!templateFormErrors.time_window ||
                        !!templateFormErrors.brand
                    ) {
                        // add timeout to prevent infinite update
                        const timeout = setTimeout(() => {
                            setShowError(true)
                            validateForm(formikBag, false)
                        }, 0)
                        return () => clearTimeout(timeout)
                    }
                    return
                    // add stepOptions on dependecies to solve issue when new option created after trigger error
                }, [templateFormErrors, stepOptions])
                const validateSpecificField: OnFormikValueChangeProps['validateSpecificField'] = async fieldNames => {
                    const errors: Awaited<ReturnType<OnFormikValueChangeProps['validateSpecificField']>> = {}
                    await Promise.all(
                        fieldNames.map(async fieldName => {
                            try {
                                await validationSchema.validateAt(fieldName, formikBag.values)
                            } catch (error) {
                                if (error instanceof yup.ValidationError) {
                                    errors[fieldName] = error.message
                                }
                            }
                        }),
                    )
                    formikBag.setErrors(errors)
                    return errors
                }
                return (
                    <Form style={{ height: '100%' }}>
                        <TemplateContainer showDrawer={showDrawer}>
                            <div className="top-block">
                                <div className="content-block">
                                    <Text scale="H3" color="primary">
                                        {t('campaign.template.step2.title')}
                                    </Text>
                                    {campaign && <CampaignStatusLabel status={campaign.status} />}
                                </div>

                                <div style={{ display: 'flex', gap: 8 }}>
                                    <IconButton
                                        form="outlined"
                                        size="s"
                                        color="neutral"
                                        icon="eye"
                                        iconForm="regular"
                                        onClick={onClickDrawer('preview')}
                                        isActivated={isAppPreviewOpened}
                                        type="button"
                                    />
                                    <IconButton
                                        form="outlined"
                                        size="s"
                                        color="neutral"
                                        icon="chatText"
                                        iconForm="regular"
                                        onClick={onClickDrawer('comments')}
                                        isActivated={isCommentCenterOpened}
                                        type="button"
                                    />
                                </div>
                            </div>

                            <div className="nav-bar-content">
                                <div className="small-sidebar-content">
                                    <div className="open-small-sidebar">
                                        <Text scale="bodyS500" color="primary">
                                            {t('campaignEditor.createTask.currentPageMobilePageTitle')}
                                        </Text>

                                        <div
                                            className="current-page-open-button"
                                            onClick={(): void => {
                                                setShowSidebarTask(true)
                                            }}
                                        >
                                            <Text scale="bodyS400" color="primary">
                                                {stepOptions[selected].title}
                                            </Text>
                                            <Icon.SelectorDownIcon />
                                        </div>
                                    </div>
                                </div>
                                <SideNavigator
                                    stepOptions={stepOptions}
                                    selected={selected}
                                    onSelectStep={onSelectStep}
                                    isDisabledClickable={true}
                                    onClickAllOption={handleClickSidebar}
                                    isExpanded={windowSize <= 1024 ? !showDrawer : true}
                                    className="side-navigator"
                                />
                                <div className="sidebar-divider" />
                                <div className="form-content" ref={formContent}>
                                    <div className="form-fields">
                                        {selected === 0 && (
                                            <CampaignInformation
                                                toNextSection={toNextSection}
                                                showError={showError}
                                                validateSpecificField={validateSpecificField}
                                                updateCurrentStepStatus={updateCurrentStepStatus}
                                                setNavigationDisable={setIsDisableNavigation}
                                                isNavigationDisable={isDisableNavigation}
                                                openWarningClickToast={openWarningClickToast}
                                                isReadOnly={isReadOnly}
                                            />
                                        )}
                                        {selected === 1 && (
                                            <CampaignSchedule
                                                toNextSection={toNextSection}
                                                backPreviousSection={backPreviousSection}
                                                showError={showError}
                                                validateSpecificField={validateSpecificField}
                                                updateCurrentStepStatus={updateCurrentStepStatus}
                                                setNavigationDisable={setIsDisableNavigation}
                                                isNavigationDisable={isDisableNavigation}
                                                openWarningClickToast={openWarningClickToast}
                                                isReadOnly={isReadOnly}
                                            />
                                        )}
                                        {selected > 1 && renderElementByKey(selected, validateSpecificField, formikBag)}
                                    </div>
                                    {typeof templateDataUuid === 'string' && (
                                        <TemplateActions templateUuid={templateDataUuid} />
                                    )}
                                </div>
                                {showDrawer && <div className="sidebar-divider" />}

                                <div className={`right-block ${showDrawer ? 'show' : ''}`}>
                                    <div className="right-block-inner-container">
                                        {isCommentCenterOpened && <CommentsCenter className="comments-center" />}
                                        {isAppPreviewOpened && (
                                            <>
                                                <div className="preview-title-block">
                                                    {isAppPreviewOpened && isMobileSize ? (
                                                        <IconButton
                                                            form="icon"
                                                            size="s"
                                                            color="neutral"
                                                            icon="x"
                                                            iconForm="regular"
                                                            onClick={() => {
                                                                setShowDrawer(!showDrawer)
                                                                updateToggleCampaignEditorDrawer('preview')
                                                            }}
                                                            isActivated={showDrawer}
                                                            type="button"
                                                            className="preview-close"
                                                        />
                                                    ) : null}
                                                    <div className="app-preview-title">
                                                        <Text scale="H4" color="primary">
                                                            {t('campaign.template.partipostPreview.title')}
                                                        </Text>
                                                        <InfoTooltip
                                                            label={t('campaign.template.partipostPreview.subText')}
                                                            iconColor="primary"
                                                            size="24px"
                                                            marginLeft="0"
                                                        />
                                                    </div>
                                                </div>
                                                {isCampaignDetail ? (
                                                    <PreviewContainer>
                                                        <CampaignDetail />
                                                    </PreviewContainer>
                                                ) : (
                                                    <PreviewContainer stepNumber={stepNumber}>
                                                        {PreviewElement ? <PreviewElement /> : null}
                                                    </PreviewContainer>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <Toast message={infoToastMessage} open={openInfoToast} onClose={handleClose} />
                            {showSidebarTask && (
                                <SmallScreenSideBar
                                    className={`small-device-sidebar ${showSidebarTask && 'show-small-sidebar'}`}
                                    id="small-screen-sidebar-container"
                                    onClick={(e: any): void => {
                                        if (e.target.id === 'small-screen-sidebar-container') {
                                            setShowSidebarTask(false)
                                        } else {
                                            return
                                        }
                                    }}
                                >
                                    <SideNavigator
                                        stepOptions={stepOptions}
                                        selected={selected}
                                        onSelectStep={onSelectStep}
                                        isDisabledClickable={true}
                                        className="side-navigator-content"
                                    />
                                </SmallScreenSideBar>
                            )}
                        </TemplateContainer>
                    </Form>
                )
            }}
        </Formik>
    )
}

export default TemplateDetail
